<template>
    <div class="border bg-gray-100 rounded p-4 mt-3">
        <div class="mb-4">List with icons / numbers</div>
        <div class="w-full">
            <Select
                label="List Type"
                name="type"
                v-model="form.type"
                class="ml-3"
                :options="[
                    { value: 'with_icon', name: 'With Icons' },
                    { value: 'with_number', name: 'With Numbers' },
                ]"
                @input="typeChanged"
            />
        </div>
        <div class="w-full">
            <Select
                label="List Style"
                name="style"
                v-model="form.style"
                class="ml-3"
                :options="[
                    { value: 'default', name: 'Default' },
                    { value: 'with_separator', name: 'With Separator' },
                    { value: 'grid', name: 'Grid' },
                ]"
            />
        </div>
        <draggable
            v-model="form.items"
            ghost-class="bg-gray-200"
            handle=".handle"
            @choose="froalaStore.disableFroala = true"
            @unchoose="froalaStore.disableFroala = false"
        >
            <div v-for="(item, index) in form.items"
                 :key="`orderedList-${index}`"
                 class="relative mt-2 max-w-4xl item">
                    <div class="flex items-center">
                        {{ index + 1}}

                        <media-reference
                            v-if="form.type === 'with_icon'"
                            :name="`ordered_list_[${index}][icon]`"
                            v-model="item.icon"
                            class="ml-3"
                            :media-types="['Icon']"
                            @input="updateIcon(index, $event)"
                        />

                        <div class="ml-3 w-full">
                            <input-text
                                label="Title"
                                :name="`ordered_list_[${index}][title]`"
                                :maxlength="255"
                                v-model="item.title"
                            />
                            <input-rich-text
                                :name="`ordered_list_[${index}][content]`"
                                v-model="item.content"
                                :language="language"
                            />
                        </div>
                        <div
                            @click="handleDelete(index)"
                            class="ml-2 cursor-pointer"
                        >
                            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </div>
                        <div class="ml-4 handle" title="Drag to reorder items">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 17l-4 4m0 0l-4-4m4 4V3" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7l4-4m0 0l4 4m-4-4v18" />
                            </svg>
                        </div>
                    </div>
                    <error class="mt-1 mtb-2" :error="errors[index]"/>
                </div>
            <button slot="footer"
                class="mt-6 text-indigo-600 hover:text-indigo-900"
                type="button"
                @click="AddItem"
            >
                + Add list item
            </button>
        </draggable>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import siteLanguage from '../../config/siteLanguage'
import InputText from "../form/InputText.vue";
import {useFroalaStore} from "../../stores/froala";

export default {
    components: {InputText, draggable },
    name: "OrderedList",
    props: {
        value: Object,
        errorBag: {
            type: [Object, Array],
            required: false,
        },
        language: {
            type: Object,
            default: () => {
                return { iso: siteLanguage.iso.ENGLISH };
            },
        },
        contentBlockId: Number,
        contentSectionId: Number,
    },
    data() {
        return {
            form: {
                type: this.value?.type ?? 'with_icon',
                style: this.value?.style ?? 'default',
                items: this.value?.items ?? [this.defaultItem()],
            },
            froalaStore: useFroalaStore(),
        };
    },
    updated() {
        this.$emit("input", this.form);
    },
    methods: {
        defaultItem() {
            return {
                title: null,
                content: "",
                icon: null,
                icon_id: null,
            };
        },
        AddItem(e) {
            e.preventDefault();
            this.form.items.push(this.defaultItem());
        },
        handleDelete: function (indexOfItemToDelete) {
            if (confirm('Are you sure you want to remove this list item?')) {
                this.form.items.splice(indexOfItemToDelete, 1);
            }
        },
        updateIcon: function (index, icon) {
            this.form.items[index].icon_id = (icon && icon.id) ? icon.id : null;
        },
        typeChanged: function (type) {
            if (type !== 'with_icon' && this.form.items.filter(item => !!item.icon_id).length) {
                this.$confirm({
                    message: `Change list type? This will remove all icons`,
                    button: {
                        no: "No",
                        yes: "Yes",
                    },
                    callback: (confirm) => {
                        if (confirm) {
                            this.form.items = this.form.items.map(item => { item.icon_id = null; item.icon = null; return item; });
                        }
                        else {
                            this.form.type = 'with_icon';
                        }
                    },
                });
            }
        },
    },
    computed: {
        errorKey: function() {
            return `contentBlocks.${this.contentBlockId}.sections.${this.contentSectionId}.ordered_list`;
        },
        errors: function() {
            let errors = {};

            for (const key of Object.keys(this.errorBag)) {
                if (key.startsWith(this.errorKey)) {
                    let li_index = key.replace(this.errorKey + '.', '').split('.')[0],
                        value = this.errorBag[key].join(' '),
                        curr_value = errors[li_index] ? errors[li_index] + ' ' : '';
                    errors[li_index] = curr_value + value;
                }
            }

            return errors;
        }
    },
    watch: {
        value: function (newValue) {
            this.form.type =  this.value?.type ?? 'with_icon';
            this.form.style = this.value?.style ?? 'default';
            this.form.items = this.value?.items ?? [this.defaultItem()];
        },
    }
};
</script>
